import { anyLeague } from "../../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../../entities/index.js";
import { DataSection, Lineup, PitchCall, TaggingData, LineupType, PitchData, ResultType, PitchingRankingStats, ResultDataNew, RunnerSafe, ScoringData, Substitution } from "../../../enums/index.js";
import EnumValueProvider from "../../../providers/EnumValueProvider.js";
import RankingStatCalculatorBase from "./../RankingStatCalculatorBase.js";
import NumberFormatter from "../../../formatters/NumberFormatter.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import sessionWithStatGrabScoring from "../../../classificators/session/sessionWithStatGrabScoring.js";
import pitchingScopeContext from "../../../classificators/resultsSlice/pitchingScopeContext.js";
import resultsSliceForPitcher from "../../../classificators/resultsSlice/resultsSliceForPitcher.js";
import classifiesAsEarnedRun from "../../../classificators/runnerSafeType/classifiesAsEarnedRun.js";
import classifiesAsRunOnError from "../../../classificators/runnerSafeType/classifiesAsRunOnError.js";
import classifiesAsHomeRun from "../../../classificators/plateAppearanceResult/classifiesAsHomeRun.js";
import classifiesAsUnearnedRun from "../../../classificators/plateAppearanceResult/classifiesAsUnearnedRun.js";
import classifiesAsWalk from "../../../classificators/plateAppearanceResult/classifiesAsWalk.js";
import classifiesAsAirBall from '../../../classificators/battedBallType/classifiesAsAirBall.js';
import classifiesAsFieldersChoice from './../../../classificators/plateAppearanceResult/classifiesAsFieldersChoice.js';

/**
 * This class is calculating earned runs for a pitcher from entire game
 * these documents can be helpful, please also review comments in code
 * https://www.cncmsbl.com/rules/calculating-earned-runs/
 * https://armorypitching.com/earned-run/
 * https://baseball.fandom.com/wiki/Earned_run
 * https://www.mlb.com/glossary/standard-stats/earned-run
 * https://baseballrulesacademy.com/official-rule/mlb/9-16-earned-runs-runs-allowed/
 */
class NewEarnedRunsCalculator extends RankingStatCalculatorBase {
    constructor(
        customResultsProcessor, rankingStatCaseVerifier, numberFormatter,
        earnedRunClassifier, homeRunClassifier, unearnedRunClassifier, walkClassifier,
        fieldersChoiceClassifier, airBallClassifier, runOnErrorClassifier
    ) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(earnedRunClassifier) !== 'undefined' && typeof(earnedRunClassifier) !== 'function') {
            throw new TypeError('earnedRunClassifier must be an function or undefined');
        }
        if (typeof(homeRunClassifier) !== 'undefined' && typeof(homeRunClassifier) !== 'function') {
            throw new TypeError('homeRunClassifier must be an function or undefined');
        }
        if (typeof(unearnedRunClassifier) !== 'undefined' && typeof(unearnedRunClassifier) !== 'function') {
            throw new TypeError('unearnedRunClassifier must be an function or undefined');
        }
        if (typeof(walkClassifier) !== 'undefined' && typeof(walkClassifier) !== 'function') {
            throw new TypeError('walkClassifier must be an function or undefined');
        }
        if (typeof(airBallClassifier) !== 'undefined' && typeof(airBallClassifier) !== 'function') {
            throw new TypeError('airBallClassifier must be an function or undefined');
        }
        if (typeof(fieldersChoiceClassifier) !== 'undefined' && typeof(fieldersChoiceClassifier) !== 'function') {
            throw new TypeError('fieldersChoiceClassifier must be an function or undefined');
        }
        if (typeof(runOnErrorClassifier) !== 'undefined' && typeof(runOnErrorClassifier) !== 'function') {
            throw new TypeError('runOnErrorClassifier must be an function or undefined');
        }

        super(
            [ PitchingRankingStats.EarnedRuns ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithStatGrabScoring,
                satisfiesAll(
                    pitchingScopeContext,
                    resultsSliceForPitcher
                )
            )
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.earnedRunClassifier = earnedRunClassifier || classifiesAsEarnedRun;
        this.homeRunClassifier = homeRunClassifier || classifiesAsHomeRun;
        this.unearnedRunClassifier = unearnedRunClassifier || classifiesAsUnearnedRun;
        this.walkClassifier = walkClassifier || classifiesAsWalk;
        this.airBallClassifier = airBallClassifier || classifiesAsAirBall;
        this.fieldersChoiceClassifier = fieldersChoiceClassifier || classifiesAsFieldersChoice;
        this.runOnErrorClassifier = runOnErrorClassifier || classifiesAsRunOnError;

        this.inningNrPath = ResultDataNew.getDataPath(
            PitchData.BeforePlayInning.key, DataSection.Scoring.key
        );
        this.inningIsTopPath = ResultDataNew.getDataPath(
            PitchData.BeforePlayInningIsTop.key, DataSection.Scoring.key
        );
        this.runnerOnFirstPath = ResultDataNew.getDataPath(
            ScoringData.Runner1Id.key, DataSection.Scoring.key
        );
        this.runnerOnSecondPath = ResultDataNew.getDataPath(
            ScoringData.Runner2Id.key, DataSection.Scoring.key
        );
        this.runnerOnThirdPath = ResultDataNew.getDataPath(
            ScoringData.Runner3Id.key, DataSection.Scoring.key
        );
        this.outsAfterPlayPath = ResultDataNew.getDataPath(
            PitchData.AfterPlayOuts.key, DataSection.Scoring.key
        );
        this.errorsAfterPlayPath = ResultDataNew.getDataPath(
            PitchData.AfterPlayErrors.key, DataSection.Scoring.key
        );
        this.errorsBeforePlayPath = ResultDataNew.getDataPath(
            PitchData.BeforePlayErrors.key, DataSection.Scoring.key
        );
        this.paResultPath = ResultDataNew.getDataPath(
            ScoringData.PlateAppearanceResult.key, DataSection.Scoring.key
        );
        this.battedBallTypePath = ResultDataNew.getDataPath(
            TaggingData.BattedBallType.key, DataSection.Tagging.key, ResultType.Pitch.key
        );
        this.pitchCallPath = ResultDataNew.getDataPath(
            ScoringData.PitchCall.key, DataSection.Scoring.key
        );
        this.runnersSafePath = ResultDataNew.getDataPath(
            ScoringData.RunnerSafe.key, DataSection.Scoring.key
        );
    }

    /**
     * Calculates given statistical parameter
     * @param {object} parameter - a parameter to calculate, object from Enum PitchingRankingStats, BattingRankingStats or GeneralRankingStats
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {RankingStat|null} RankingStat instance or null when calculation wasn't possible
     */
    calculateStat(parameter, slice, session, league) {
        const results = slice.getBiggestSlice();
        const pitcherId = slice.scope.tagId;
        const resultsPerInningHalf = this.resultsProcessor.groupData(
            results, [this.inningIsTopPath]
        );
        let earnedRuns = 0;

        for (const inningIsTop in resultsPerInningHalf) {
            if (typeof(inningIsTop) === 'undefined' || Number.isNaN(inningIsTop)) {
                // TODO - maybe throw ? this could end processing quickly for non tagged data
                continue;
            }

            let possibleEarnedRuns = [];
            let prevInningNumber = 0;
            let offensiveTeamLineup = null;

            for (let r = 0; r < resultsPerInningHalf[inningIsTop].length; r++) {
                const result = results[r];
                const scoredInResult = [];
                const inningNumber = this.resultsProcessor.getObjectContent(
                    result, this.inningNrPath
                );
                if (typeof(inningNumber) === 'undefined' || Number.isNaN(inningNumber)) {
                    // TODO - maybe silently continue ? On the other hand we have inningIsTop but no InningNr ?
                    throw new Error(`Inning number for result ${result[ResultDataNew.Id.key]} is 0 or NaN`);
                }

                if (offensiveTeamLineup === null) {
                    offensiveTeamLineup = this.getLineupForOffensiveTeam(result, session.lineups);
                }

                if (inningNumber != prevInningNumber) {
                    // we are starting new inning...
                    // check which runs are actually earned for current pitcher
                    // @see https://baseballrulesacademy.com/official-rule/mlb/9-16-earned-runs-runs-allowed/
                    // Rule 9.16 (a) An earned run is a run for which the pitcher is held accountable. In determining earned runs, the official scorer shall reconstruct the inning without errors (which exclude catcher's interference) and passed balls, giving the benefit of the doubt always to the pitcher in determining which bases would have been reached by runners had there been error-less play.
                    // (a) The official scorer shall charge an earned run against a pitcher every time a runner reaches home base by the aid of safe hits, sacrifice bunts, a sacrifice fly, stolen bases, put outs, fielder's choices, base on balls, hit batters, balks or wild pitches,(including a wild pitch on a third strike that permits a batter to reach first base) before fielding chances have been offered to put out the offensive team.
                    // @see https://baseball.fandom.com/wiki/Earned_run
                    const earnedRunsBy = this.listEarnedRuns(
                        pitcherId, possibleEarnedRuns, resultsPerInningHalf[inningIsTop], offensiveTeamLineup
                    );
                    // increase counter
                    earnedRuns += earnedRunsBy.reduce((earnedRuns, resultWithRuns) => {
                        earnedRuns += resultWithRuns.runnerIds.length;
                        return earnedRuns;
                    }, 0);
                    // erase collection of possible earned runs
                    possibleEarnedRuns = [];
                    prevInningNumber = inningNumber;
                }

                if (
                    !result || !result[ResultDataNew.Data.key]
                    || !result[ResultDataNew.Data.key][DataSection.Scoring.key]
                ) {
                    continue;
                }
    
                if (!result[ResultDataNew.Data.key][DataSection.Scoring.key][ScoringData.RunnerSafe.key]) {
                    continue;
                }

                // A run is counted as unearned when:... 
                // A baserunner scores after the third out would have been made.
                // @see https://baseball.fandom.com/wiki/Earned_run
                const runnersSafe = this.resultsProcessor.getObjectContent(result, this.runnersSafePath) || [];
                for (let rs = 0; rs < runnersSafe.length; rs++) {
                    // @see https://baseballrulesacademy.com/official-rule/mlb/9-16-earned-runs-runs-allowed/
                    const runnerAdv = runnersSafe[rs];
    
                    // Rule 9.16 (a) An earned run is a run for which the pitcher is held accountable. In determining earned runs, the official scorer shall reconstruct the inning without errors (which exclude catcher's interference) and passed balls, giving the benefit of the doubt always to the pitcher in determining which bases would have been reached by runners had there been error-less play.
                    // (a) The official scorer shall charge an earned run against a pitcher every time a runner reaches home base by the aid of safe hits, sacrifice bunts, a sacrifice fly, stolen bases, put outs, fielder's choices, base on balls, hit batters, balks or wild pitches,(including a wild pitch on a third strike that permits a batter to reach first base) before fielding chances have been offered to put out the offensive team.
                    // @see https://baseball.fandom.com/wiki/Earned_run
                    if (!runnerAdv || runnerAdv[RunnerSafe.TargetBase.key] !== 4) {
                        continue;
                    }
                    if (this.earnedRunClassifier(runnerAdv[RunnerSafe.Type.key])) {
                        // check how the runner got here, as he reached home (scored) without an error
                        scoredInResult.push(runnerAdv[RunnerSafe.RunnerId.key]);
                    }
                    else if (this.wouldRunnerScoreAnyway(runnerAdv, result, results)) {
                        // While the inning is still being played, this last scenario can cause a temporary situation where a run has already scored, but its earned/unearned status is not yet certain. For example, with two outs, a runner on third base scores on a passed ball. For the time being, the run is unearned since the runner should still be at third. If the batter strikes out to end the inning, it will stay that way.
                        // If the batter gets a base hit, which would have scored the runner anyway, the run now becomes earned.
                        scoredInResult.push(runnerAdv[RunnerSafe.RunnerId.key]);
                    }
                }

                // let's check home run case separately, if app will not send runnerSafe entry for this type of action...
                const paResult = this.resultsProcessor.getObjectContent(result, this.paResultPath);
                const batterId = result[ResultDataNew.BatterId.key];
                if (batterId && this.homeRunClassifier(paResult) && scoredInResult.indexOf(batterId) === -1) {
                    earnedRuns++;
                    // scoredInResult.push(batterId); -- this could increase it...
                }

                possibleEarnedRuns.push(
                    this.buildRunInfo(result, scoredInResult)
                );
            }
        }

        if (earnedRuns > 0) {
            const earnedRunsParam = this.supportedParams[0];
            return RankingStat.fromParameter(
                earnedRunsParam, this.numberFormatter.format(earnedRuns), earnedRuns
            );
        }

        return null;
    }

    buildRunInfo(result, runnerIds) {
        return {
            result, runnerIds
        };
    }

    listResultsForPlateApperance(resultsPerInningHalf, finalResultInPa) {
        const batterId = finalResultInPa[ResultDataNew.BatterId.key];
        const finalResultIndex = resultsPerInningHalf.indexOf(finalResultInPa);
        let index = finalResultIndex;

        for (; index >= 0; ) {
            // TODO - should we also break this loop when inning has changed ? Batter can stay at-bat between innings without PA result (runner caught stealing or pickoff)
            if (resultsPerInningHalf[index][ResultDataNew.BatterId.key] !== batterId) {
                break;
            }

            index--;
        }

        return resultsPerInningHalf.slice(index, finalResultIndex);
    }

    determinePitcherForEarnedRun(plateAppearanceResults, deffensiveTeamLineup) {
        const finalPitch = plateAppearanceResults[plateAppearanceResults.length-1];
        const finalPitchBy = finalPitch[ResultDataNew.PitcherId.key];
        const paResult = this.resultsProcessor.getObjectContent(
            finalPitch, this.paResultPath
        );

        for (let i = plateAppearanceResults.length-1; i >= 0; i--) {
            const prevResult = plateAppearanceResults[i];
            const pitchBy = prevResult[ResultDataNew.PitcherId.key];
            if (pitchBy !== finalPitchBy) {
                // so the pitcher has been substituted, we need to check the count and result..
                // now the count and type of PA will matter... @see https://baseballrulesacademy.com/official-rule/mlb/9-16-earned-runs-runs-allowed/ point h
                if (this.walkClassifier(paResult)) {
                    // (h) A relief pitcher shall not be held accountable when the first batter to whom he pitches reaches first base
                    // on four called balls if such batter has a decided advantage in the ball and strike count when pitchers are changed.
                    const balls = prevResult[ResultDataNew.Data.key][DataSection.Scoring.key][PitchData.BeforePlayBalls.key];
                    const strikes = prevResult[ResultDataNew.Data.key][DataSection.Scoring.key][PitchData.BeforePlayStrikes.key];
    
                    if (
                        // 2 balls, no strike, 2 balls, 1 strike
                        (balls === 2 && strikes < 2)
                        ||
                        (balls === 3 && strikes < 3)
                    ) {
                        // and the batter gets a base on balls, the Official Scorer shall charge that batter and the base on balls to the preceding pitcher, not to the relief pitcher.
                        return pitchBy;
                    }
                }
            }
        }

        // (2) Any other action by such batter, such as reaching base on a hit, an error, a fielder’s choice, a force-out, or being touched by a pitched ball, shall cause such a batter to be charged to the relief pitcher.
        return finalPitchBy;
    }

    didBatterAdvancedWithoutError(batterAdvancedOnResult) {
        const paResult = this.resultsProcessor.getObjectContent(
            batterAdvancedOnResult, this.paResultPath
        );

        // A run is counted as unearned when:
        if (
            // A batter reaches base on an error (including catcher's interference), and later scores a run in that inning.
            // A batter reaches first base on a passed ball (but not a wild pitch) and later scores.
            // @see https://en.wikipedia.org/wiki/Earned_run
            // @see https://armorypitching.com/earned-run/
            this.unearnedRunClassifier(paResult)
        ) {
            return false;
        }

        return true;
    }

    isInningExtendedByDroppedFoulFly(plateAppearanceResults) {
        // A run is unearned when... @see https://armorypitching.com/earned-run/
        // A batter hits a foul fly ball (when the infield fly rule is not in play) that is dropped by a fielder for an error,
        // extending the at bat, and later scores a run in that inning by any means. In this case,
        // the manner in which the batter reached base becomes irrelevant.

        for (let i = 0; i < plateAppearanceResults.length; i++) {
            const result = plateAppearanceResults[i];
            const battedBallType = this.resultsProcessor.getObjectContent(
                result, this.battedBallTypePath
            );

            // check for fly ball, line drive or pop up
            if (!this.airBallClassifier(battedBallType)) {
                continue;
            }

            // check for foul ball
            const pitchCall = this.resultsProcessor.getObjectContent(
                result, this.pitchCallPath
            );

            if (pitchCall !== PitchCall.FoulBall.key && pitchCall !== PitchCall.FoulBunt.key) {
                continue;
            }

            // finally, check for errors on this play
            const errorsBefore = this.resultsProcessor.getObjectContent(
                result, this.errorsBeforePlayPath
            );
            const errorsAfter = this.resultsProcessor.getObjectContent(
                result, this.errorsAfterPlayPath
            );

            if (typeof(errorsBefore) !== 'number' || typeof(errorsAfter) !== 'number') {
                throw new TypeError(`Unable to determine errros in play for result ${result[ResultDataNew.Id.key]}`);
            }

            if (errorsAfter > errorsBefore) {
                return true;
            }
        }

        return false;
    }

    didRunnerAdvancedOnFieldersChoiceWithError(resultsPerInningHalf, scoredOnResult, runnerId, offensiveTeamLineup) {
        // A score is unearned when... @see https://en.wikipedia.org/wiki/Earned_run
        // A baserunner remains on base or advances to the next base as the result of an error on a fielder's choice play that would put
        // the baserunner out except for the error, and later scores.
        const resultIndex = resultsPerInningHalf.indexOf(scoredOnResult);
        if (resultIndex === -1) {
            throw new Error(`Unable to determine result's ${scoredOnResult[ResultDataNew.Id.key]} index in results when calling didRunnerAdvancedOnFieldersChoiceWithError`);
        }

        let trackedRunnerId = runnerId;

        // check for a home run case
        if (runnerId === scoredOnResult[ResultDataNew.BatterId.key]) {
            return false;
        }

        // now in order to check how batter advanced we need to iterate backward
        for (let r = resultIndex - 1; r >= 0; r--) {
            const prevResult = resultsPerInningHalf[r];
            // check how runner advanced
            const runnersSafe = this.resultsProcessor.getObjectContent(prevResult, this.runnersSafePath) || [];
            let runnerSafeOn = null;

            for (let rs = 0; rs < runnersSafe.length; rs++) {
                const runnerSafe = runnersSafe[rs];
                if (runnerSafe[RunnerSafe.RunnerId.key] !== trackedRunnerId) {
                    continue;
                }
                runnerSafeOn = runnerSafe;
            }

            if (runnerSafeOn === null) {
                // woohohoh, so we need to look for pinch runner substitution
                const substitution = this.getSubstitutionBefore(
                    (resultsPerInningHalf[r+1] || scoredOnResult)[ResultDataNew.CreateDate.key],
                    trackedRunnerId, 
                    offensiveTeamLineup
                );

                if (!substitution) {
                    throw new Error(`Found discontinuity on result ${prevResult[ResultDataNew.Id.key]} when analyzing runner ${runnerId} history of advancing for run scored in result ${scoredOnResult[ResultDataNew.Id.key]}`);
                }

                trackedRunnerId = substitution[Substitution.ReplacedTagID.key];
                // repeat check...
                r++;
                continue;
            }

            const paResult = this.resultsProcessor.getObjectContent(
                prevResult, this.paResultPath
            );
            if (!this.fieldersChoiceClassifier(paResult)) {
                continue;
            }

            const runnerSafe = runnerSafeOn[RunnerSafe.Type.key];
            if (this.runOnErrorClassifier(runnerSafe)) {
                return true;
            }
        }

        return false;
    }

    listEarnedRuns(pitcherId, possibleEarnedRuns, resultsPerInningHalf, offensiveTeamLineup) {
        const earnedRunsByPitcher = [];
        for (let i = 0; i < possibleEarnedRuns.length; i++) {
            const { result, runnerIds } = possibleEarnedRuns[i];
            const runsEarnedBy = [];

            for (let r = 0; r < runnerIds.length; r++) {
                const runnerId = runnerIds[r];

                const resultIndex = resultsPerInningHalf.indexOf(result);
                if (resultIndex === -1) {
                    throw new Error(`Unable to determine result's ${result[ResultDataNew.Id.key]} index in inning half`)
                }

                const batterAdvancedOn = this.getResultOnWhichBatterBecomeRunner(
                    result, runnerId, resultsPerInningHalf, offensiveTeamLineup
                );

                if (!batterAdvancedOn) {
                    throw new Error(`Unable to determine how runner ${runnerId} advanced to first base before scoring on result ${result[ResultDataNew.Id.key]}`);
                }

                // now we need to check if batter advanced on error or catcher's interference
                if (!this.didBatterAdvancedWithoutError(batterAdvancedOn)) {
                    continue;
                }

                // even home run can be unearned...
                // On the other hand, a batter/runner may make his entire circuit around the bases without the aid of an error,
                // yet the run would be counted as unearned if an error prevented the third out from being made before he came up to bat.
                // @see https://armorypitching.com/earned-run/
                if (this.shouldIningBeOverEarlier(resultsPerInningHalf, batterAdvancedOn)) {
                    continue;
                }

                // unfortunately we cannot simply compare pitcherId, as the pitcher could be substituted in this PA,
                // which requires checking the count at which substitution has been made
                const plateAppearanceResults = this.listResultsForPlateApperance(
                    resultsPerInningHalf, batterAdvancedOn
                );

                // check for dropped foul fly ball... at bat extended by dropped foul fly cannot generate earned runs
                if (this.isInningExtendedByDroppedFoulFly(plateAppearanceResults)) {
                    continue;
                }

                // check if runner advanced or remained on base because of fielders choice and an error, this run will also be unearned
                if (this.didRunnerAdvancedOnFieldersChoiceWithError(resultsPerInningHalf, result, runnerId, offensiveTeamLineup)) {
                    continue;
                }

                const runEarnedBy = this.determinePitcherForEarnedRun(
                    plateAppearanceResults
                );

                // check if we are looking for this pitcher...
                if (pitcherId !== runEarnedBy) {
                    continue;
                }

                // check fo duplicates, just in case (it shouldn't happen if data from app is ok)
                if (runsEarnedBy.indexOf(runnerId) !== -1) {
                    continue;
                }

                // add id of runner who scored in this result
                runsEarnedBy.push(runnerId);
            }

            earnedRunsByPitcher.push(
                this.buildRunInfo(result, runsEarnedBy)
            );
        }

        return earnedRunsByPitcher;
    }

    shouldIningBeOverEarlier(resultsPerInningHalf, analyzeBeforeResult) {
        // On the other hand, a batter/runner may make his entire circuit around the bases without the aid of an error,
        // yet the run would be counted as unearned if an error prevented the third out from being made before he came up to bat.
        // @see https://armorypitching.com/earned-run/

        const resultIndex = resultsPerInningHalf.indexOf(analyzeBeforeResult);
        if (resultIndex === -1) {
            throw new Error(`Unable to determine result's ${analyzeBeforeResult[ResultDataNew.Id.key]} index in inning half when calling shouldIningBeOverEarlier`);
        }

        // now in order to check whether innin should be over without error on 2 outs we need to iterate backward
        for (let r = resultIndex - 1; r >= 0; r--){
            const prevResult = resultsPerInningHalf[r];

            const outsAfterPlay = this.resultsProcessor.getObjectContent(
                prevResult, this.outsAfterPlayPath
            );
            const errorsAfter = this.resultsProcessor.getObjectContent(
                prevResult, this.errorsAfterPlayPath
            );
            const errorsBefore = this.resultsProcessor.getObjectContent(
                prevResult, this.errorsBeforePlayPath
            );

            if ((outsAfterPlay + errorsAfter - errorsBefore) >= 3) {
                return true;
            }

            return false;
        }
    }

    getResultOnWhichBatterBecomeRunner(scoredOnResult, runnerId, resultsPerInningHalf, offensiveTeamLineup) {
        const resultIndex = resultsPerInningHalf.indexOf(scoredOnResult);
        if (resultIndex === -1) {
            throw new Error(`Unable to determine result's ${scoredOnResult[ResultDataNew.Id.key]} index in inning half when calling getResultOnWhichBatterBecomeRunner`);
        }

        let trackedRunnerId = runnerId;

        // check for a home run case
        if (runnerId === scoredOnResult[ResultDataNew.BatterId.key]) {
            return scoredOnResult;
        }

        // now in order to check how batter advanced we need to iterate backward
        for (let r = resultIndex - 1; r >= 0; r--) {
            const prevResult = resultsPerInningHalf[r];
            // check if runner is still present in runners on bases
            // TODO - not sure what to do when runner advances on error or passed ball, asked Domain Experts, waiting for reply
            const runnerOnThird = this.resultsProcessor.getObjectContent(
                prevResult, this.runnerOnThirdPath
            );
            if (runnerOnThird === trackedRunnerId) {
                continue;
            }
            const runnerOnSecond = this.resultsProcessor.getObjectContent(
                prevResult, this.runnerOnSecondPath
            );
            if (runnerOnSecond === trackedRunnerId) {
                continue;
            }
            const runnerOnFirst = this.resultsProcessor.getObjectContent(
                prevResult, this.runnerOnFirstPath
            );
            if (runnerOnFirst === trackedRunnerId) {
                continue;
            }

            // so maybe he was a batter and hit a home run ?
            const batterId = prevResult[ResultDataNew.BatterId.key];
            if (batterId === trackedRunnerId) {
                return prevResult;
            }

            // woohohoh, so we need to look for pinch runner substitution
            const substitution = this.getSubstitutionBefore(
                (resultsPerInningHalf[r+1] || scoredOnResult)[ResultDataNew.CreateDate.key],
                trackedRunnerId, 
                offensiveTeamLineup
            );

            if (!substitution) {
                throw new Error(`Found discontinuity on result ${prevResult[ResultDataNew.Id.key]} when analyzing runner ${runnerId} history of scoring in result ${scoredOnResult[ResultDataNew.Id.key]}`)
            }

            trackedRunnerId = substitution[Substitution.ReplacedTagID.key];
        }

        return null;
    }

    // helpers
    getLineupForOffensiveTeam(result, lineups) {
        const inningIsTop = result[ResultDataNew.Data.key][DataSection.Scoring.key][PitchData.BeforePlayInningIsTop.key] == 1;

        // see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-lineup-v2.json
        if (inningIsTop) {
            // top is batted by away team
            return lineups.find(l => l && l[Lineup.Type.key] === LineupType.Away.key);
        }

        return lineups.find(l => l && l[Lineup.Type.key] === LineupType.Home.key);
    }

    getLineupForDefensiveTeam(result, lineups) {
        const inningIsTop = result[ResultDataNew.Data.key][DataSection.Scoring.key][PitchData.BeforePlayInningIsTop.key] == 1;

        // see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-lineup-v2.json
        if (inningIsTop) {
            // top is batted by away team
            return lineups.find(l => l && l[Lineup.Type.key] === LineupType.Home.key);
        }

        return lineups.find(l => l && l[Lineup.Type.key] === LineupType.Away.key);
    }

    getSubstitutionBefore(dateTime, enteredPlayerId, lineup) {
        const dateBefore = new Date(dateTime);
        if (!dateBefore || !(dateBefore instanceof Date) || isNaN(dateBefore)) {
            throw new Error('Unable to parse date ' + dateTime);
        }

        for (let i = lineup[Lineup.Substitutions.key].length - 1; i > 0 ; i--) {
            const substitution = lineup[Lineup.Substitutions.key][i];
            if (!substitution || substitution[Substitution.EnteredTagID.key] !== enteredPlayerId) {
                continue;
            }

            const substitutionDate = new Date(substitution[Substitution.EnteredDate.key]);
            if (!substitutionDate || !(substitutionDate instanceof Date) || isNaN(substitutionDate)) {
                throw new Error('Unable to parse date ' + substitution[Substitution.EnteredDate.key]);
            }

            if (substitutionDate >= dateBefore) {
                continue;
            }

            // TODO - it assumes proper order of Lineup.Substitutions
            return substitution;
        }

        return null;
    }
}

export default NewEarnedRunsCalculator;